'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { Icon } from '@/components/ui/icon';
import { CircleArrowDown02IconSolid } from '@/components/ui/icon/glyphs';
import { PNL_DUST_D6 } from '@/lib/market/position-pnl';
import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
interface PnlDirectionArrowProps {
  /** Signed PnL magnitude in D6 — the sign picks both direction and tone. */
  valueD6: bigint;
  /** Size/extra utilities for the glyph (e.g. `size-5`). Defaults to `size-4`. */
  className?: string;
}

/**
 * Shared profit/loss direction indicator: a circle-arrow that points up for
 * profit and down for loss, tinted by sign, animating both rotation (direction)
 * and color. Honors reduced motion.
 *
 * Only the glyph lives here — the value formatting and layout stay at the call
 * site, since they differ per surface (holdings column with a percent line, the
 * position card's ticking `AnimatedNumber`, etc.). Callers also decide whether
 * to render it at all; within the neutral dust band this points down in the
 * disabled tone, so hide it there if that reads as noise.
 */
export function PnlDirectionArrow(t0) {
  const $ = _c(13);
  const {
    valueD6,
    className
  } = t0;
  const prefersReducedMotion = useReducedMotion() === true;
  const dustD6 = BigInt(PNL_DUST_D6);
  const isProfit = valueD6 > dustD6;
  const isLoss = valueD6 < -dustD6;
  const rotate = isProfit ? 180 : 0;
  const tone = isProfit ? "text-data-profit" : isLoss ? "text-data-loss" : "text-foreground-disabled";
  let t1;
  if ($[0] !== prefersReducedMotion) {
    t1 = prefersReducedMotion ? {
      duration: 0
    } : MOTION_POSITION_SMALL;
    $[0] = prefersReducedMotion;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const transition = t1;
  let t2;
  if ($[2] !== rotate) {
    t2 = {
      rotate
    };
    $[2] = rotate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== className || $[5] !== tone) {
    t3 = cn("size-4 transition-colors", tone, className);
    $[4] = className;
    $[5] = tone;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <Icon icon={CircleArrowDown02IconSolid} className={t3} />;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== transition) {
    t5 = <LazyMotion features={domAnimation}><m.span animate={t2} transition={transition} className="inline-flex">{t4}</m.span></LazyMotion>;
    $[9] = t2;
    $[10] = t4;
    $[11] = transition;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}